/**
 * EM Sticky ATC — Palo Alto cart-bar scroll/variant sync + Reviews.io refresh.
 * Also lifts Shopify Inbox when the bar is visible.
 */
(() => {
  const RATING_CONFIG = {
    store: "molten",
    mode: "default",
    color: "#f6db0b",
    linebreak: false,
    lang: "de",
    usePolaris: false,
    showEmptyStars: false,
    showNumericalAverageRating: false,
  };

  const COUNT_CLASSES = [
    "count-is-in",
    "count-is-low",
    "count-is-out",
    "count-is-unavailable",
  ];

  const money = (cents) => {
    if (cents === 0) return window.theme?.strings?.free || "Gratis";
    const format = window.theme?.settings?.currency_code_enable
      ? window.theme.moneyWithCurrencyFormat
      : window.theme.moneyFormat;
    return window.theme.formatMoney(cents, format);
  };

  const refreshStickyReviews = () => {
    if (typeof ratingSnippet !== "function") return;
    if (!document.querySelector(".em-sticky-atc .ruk_rating_snippet")) return;
    ratingSnippet("ruk_rating_snippet", RATING_CONFIG);
  };

  const setChatOffset = (height) => {
    document.documentElement.style.setProperty(
      "--em-sticky-atc-height",
      height > 0 ? `${Math.ceil(height)}px` : "0px"
    );
    document.documentElement.classList.toggle("em-sticky-atc-visible", height > 0);
  };

  class EmStickyAtc extends HTMLElement {
    connectedCallback() {
      this.section = this.closest("product-info");
      this.form = this.section?.querySelector("[data-product-form]");
      if (!this.section || !this.form) return;

      this.formWrapper =
        (window.theme?.variables?.productPageSticky &&
          this.section.querySelector("[data-form-wrapper]")) ||
        this.form;

      this.atcButtons = this.querySelectorAll("[data-em-sticky-atc]");
      this.boundClick = this.handleClick.bind(this);
      this.boundReset = this.resetLoading.bind(this);
      this.boundResize = this.syncChatOffset.bind(this);

      this.addEventListener("click", this.boundClick);
      ["theme:cart:refresh", "theme:cart-bar:reset-atc-animation"].forEach((evt) => {
        document.addEventListener(evt, this.boundReset);
      });
      window.addEventListener("theme:resize", this.boundResize);

      this.subscribeVariant();
      this.initObserver();
      refreshStickyReviews();
    }

    handleClick(event) {
      const btn = event.target.closest("[data-em-sticky-atc]");
      if (!btn) return;
      event.preventDefault();

      const mainAtc = this.form.querySelector("[data-add-to-cart]");
      if (!mainAtc || mainAtc.hasAttribute("disabled")) return;

      this.atcButtons.forEach((el) => {
        el.classList.add("is-loading");
        el.setAttribute("disabled", "true");
      });

      if (this.form.requestSubmit) {
        this.form.requestSubmit(mainAtc);
      } else {
        mainAtc.click();
      }
    }

    resetLoading() {
      this.atcButtons.forEach((el) => {
        el.classList.remove("is-loading");
        const available = el.dataset.available !== "false";
        if (available) el.removeAttribute("disabled");
      });
    }

    subscribeVariant() {
      if (!window.subscribe || !window.theme?.PUB_SUB_EVENTS?.variantChange) return;

      this.unsub = window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, ({ data }) => {
        const sectionId = data?.sectionId;
        if (
          !sectionId ||
          (sectionId !== this.section.dataset.section &&
            sectionId !== this.section.dataset.originalSection)
        ) {
          return;
        }
        this.updateFromVariant(data.variant);
        // Inventory DOM is updated by product-info after the pub/sub — sync next frame
        requestAnimationFrame(() => this.syncInventory());
        refreshStickyReviews();
      });
    }

    syncInventory() {
      const mainInv = this.section.querySelector("[data-remaining-wrapper]");
      const stickyInv = this.querySelectorAll("[data-em-sticky-inventory]");
      if (!stickyInv.length) return;

      let nextClass = "count-is-in";
      if (mainInv) {
        const found = COUNT_CLASSES.find((cls) => mainInv.classList.contains(cls));
        if (found) nextClass = found;

        const mainCount = mainInv.querySelector("[data-remaining-count]");
        stickyInv.forEach((el) => {
          const stickyCount = el.querySelector("[data-remaining-count]");
          if (mainCount && stickyCount) stickyCount.textContent = mainCount.textContent;
        });
      }

      stickyInv.forEach((el) => {
        COUNT_CLASSES.forEach((cls) => el.classList.remove(cls));
        el.classList.add(nextClass);
      });
    }

    updateFromVariant(variant) {
      if (!variant) return;

      this.querySelectorAll("[data-em-sticky-image] img").forEach((img) => {
        const src = variant.featured_media?.preview_image?.src;
        if (!src) return;
        const width = img.classList.contains("em-sticky-atc__image--mobile") ? 96 : 128;
        img.src = `${src}${src.includes("?") ? "&" : "?"}width=${width}`;
      });

      const onSale = variant.compare_at_price > variant.price;
      this.querySelectorAll("[data-em-sticky-sale]").forEach((el) => {
        el.innerHTML = money(variant.price);
        el.classList.toggle("product__price--sale", onSale);
      });
      this.querySelectorAll("[data-em-sticky-compare]").forEach((el) => {
        el.textContent = onSale ? money(variant.compare_at_price) : "";
        el.classList.toggle("hidden", !onSale);
      });

      const available = Boolean(variant.available);
      const label = available
        ? this.dataset.atcLabel || window.theme?.strings?.addToCart || "In den Warenkorb legen"
        : this.dataset.soldOutLabel || window.theme?.strings?.soldOut || "Ausverkauft";

      this.atcButtons.forEach((btn) => {
        btn.dataset.available = available ? "true" : "false";
        btn.toggleAttribute("disabled", !available);
        const text = btn.querySelector("[data-em-sticky-atc-text]");
        if (text) text.textContent = label;
      });

      // Immediate fallback before Inventory section HTML arrives
      const stickyInv = this.querySelectorAll("[data-em-sticky-inventory]");
      stickyInv.forEach((el) => {
        COUNT_CLASSES.forEach((cls) => el.classList.remove(cls));
        el.classList.add(available ? "count-is-in" : "count-is-out");
      });
    }

    syncChatOffset() {
      const height = this.classList.contains("is-visible") ? this.offsetHeight : 0;
      setChatOffset(height);
    }

    updateVisibility() {
      const show = this.pastForm && !this.footerInView;
      this.classList.toggle("is-visible", show);
      this.syncChatOffset();
    }

    initObserver() {
      if (!("IntersectionObserver" in window) || !this.formWrapper) return;

      this.pastForm = false;
      this.footerInView = false;

      this.obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.pastForm = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.updateVisibility();
          });
        },
        { rootMargin: "0px", threshold: 0 }
      );
      this.obs.observe(this.formWrapper);

      this.footerEl =
        document.querySelector(".site-footer") ||
        document.querySelector(".section-footer") ||
        document.querySelector('footer[role="contentinfo"]');

      if (this.footerEl) {
        this.footerObs = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.footerInView = entry.isIntersecting;
              this.updateVisibility();
            });
          },
          { rootMargin: "0px", threshold: 0 }
        );
        this.footerObs.observe(this.footerEl);
      }
    }

    disconnectedCallback() {
      this.obs?.disconnect();
      this.footerObs?.disconnect();
      this.unsub?.();
      this.removeEventListener("click", this.boundClick);
      window.removeEventListener("theme:resize", this.boundResize);
      ["theme:cart:refresh", "theme:cart-bar:reset-atc-animation"].forEach((evt) => {
        document.removeEventListener(evt, this.boundReset);
      });
      setChatOffset(0);
    }
  }

  if (!customElements.get("em-sticky-atc")) {
    customElements.define("em-sticky-atc", EmStickyAtc);
  }

  document.addEventListener("shopify:section:load", refreshStickyReviews);
})();
